/**
 * THE MARCADORA PANEL (`L`) and the seal vocabulary the entry step shares with it.
 *
 * The seal COLOURS are names of a physical tape on the field, so they are translated as colours and
 * never as codes: a player looking for the white tape is looking for white. The class itself (`r1`…`r5`)
 * is regulation and is NOT translated — it is the same token in every language.
 */
export default {
  title: 'MARCADORA SETUP',
  // The dial that is a MUZZLE reading, and it says so: the readout beside it carries downrange figures,
  // and two labels reading "velocity" on one screen are one word for two different magnitudes.
  fps: 'muzzle velocity',
  weight: 'BB weight',
  mode: 'fire mode',
  min_label: 'MINIMUM RANGE',
  hop: 'hop-up',
  // No field measures hop, so it is the one dial with nothing to be legal about. Saying that is what
  // stops it from being read as a fourth thing the referee checks.
  hop_note: 'no field measures it — it is tuning',
  over_cap: "over the field's ceiling",
  at_cap: '(AT THE CEILING)',
  // What the entry step already settled, said where it cannot be moved: this panel dials WITHIN the seal,
  // so the row it does not move has to name itself as one.
  fixed_at_entry: 'chosen on entry',
  // How a class fires, in the player's words. `bolt` is not a mode anyone picks — the class brings it.
  mode_auto: 'sustained fire',
  mode_single: 'one shot per click',
  mode_bolt: 'bolt action',
  // The selector's own two rows. Short because they sit side by side on buttons a thumb covers; the
  // prose above is what explains them.
  mode_auto_btn: 'FULL AUTO',
  mode_semi_btn: 'SEMI',
  // The seal colours.
  only_with: 'only with the {marker}',
  // The two REASONS a class is struck through and the field's ceiling is not what refuses it. They travel
  // marked and not hidden (§30.2), so the reason is the whole of what the row can say.
  only_plays_in: 'the {marker} only plays in {rau}',
  no_bolt_yet: 'bolt action not in the game yet',
  brings_own: 'The {marker} brings its own class: it enters as {rau} and nothing else.',
  no_min_title: 'NO MINIMUM RANGE',
  no_min_note: 'this class scores at any distance',
  // The minimum's price, beside the minimum. Since it left the advice band this is the only place it is
  // read, and a rule that VOIDS impacts may not sit where you find it only by going to look for it.
  min_warn: 'a hit any closer does NOT score and earns you a warning',
  fires_by_rule: '{rau} fires {mode} by regulation',
  cap_note: 'ceiling {rau} with {g} g: {fps} fps',
  // What leaves the muzzle, in the units the field speaks. The chronograph line exists because the table
  // states its ceilings with a 0.20 g BB: without it the figure above reads as low when it is the SAME
  // energy spread over a heavier BB.
  muzzle_line: '{mps} m/s with a {g} g BB',
  chrono_line: 'on a chronograph with 0.20 g it would read {fps} fps, which is how the table states its ceilings',
  cadence_line: '{mode} · one shot every {ms} ms',
  // ONE MODEL, AS A ROW. Two whole lines and not glued fragments, because the order is what changes
  // between languages; the minimum is a separate one since a class without it says nothing rather than
  // `MED 0 m`. `blurb` is what the iron IS — typed in the catalogue, never derived.
  model_line: '{blurb} {rau} ▪ {colour} · {mode} · {bbs} BBs',
  model_min: ' · minimum {d} m',
  model_line_free: '{blurb} {bbs} BBs · the seal is chosen separately',
  white: 'white',
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  black: 'black',
  apply: 'Apply',
  cancel: 'Cancel',

  // ── what the panel SAYS once it has asked ─────────────────────────────────────────────────────────
  // A request and its answer are two events and read as two lines: between them the page shows a setup
  // it has asked for and not one it has been granted, and collapsing them hides exactly that gap.
  note_asking: 'marcadora: asking for {rau} · {g} g · {fps} fps · hop {hop} %…',
  note_seated: 'marcadora: {setup}',
  note_refused: 'marcadora refused · {reason} — {setup} stands',
  notice_refused: 'MARCADORA REFUSED · {reason} — {setup} STANDS',
  // The re-ask on a rejoin, named as one: the authority does not carry the setup across a reconnection,
  // and a silent re-ask reads as the panel having forgotten what was picked.
  note_rejoin: 'RECONNECTED — asking for your marcadora again ({rau})',
  // The selector on a class that cannot sustain the trigger. The key ANSWERS, and what it answers is that
  // there is nothing to move (§132) — silence would be a key that works and does nothing.
  note_mode: 'fire mode: asking for {mode}…',
  no_selector: '{rau} ▪ {colour} — regulation has it on SINGLE SHOT: there is no selector to move',
};
